import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { CloudUpload } from '@mui/icons-material';
import { useCreateTrainingModule } from '../../../api/hooks/useTrainingModules';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getNextSequence } from '../utils/sequenceHelpers';

interface CreateModuleModalProps {
  open: boolean;
  onClose: () => void;
  trainingId: string;
  existingModules?: any[];
}

const CreateModuleModal: React.FC<CreateModuleModalProps> = ({
  open,
  onClose,
  trainingId,
  existingModules = [],
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [skillName, setSkillName] = useState('');
  const [progressContribution, setProgressContribution] = useState(100);
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(undefined);
  const [logoFileName, setLogoFileName] = useState('');
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateTrainingModule(
    selectedOrganization?.id || '',
    trainingId
  );

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (progressContribution < 0 || progressContribution > 100) {
      enqueueSnackbar('La contribución al progreso debe estar entre 0 y 100', {
        variant: 'error',
      });
      return;
    }

    try {
      const nextSequence = getNextSequence(existingModules);
      await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        skillName: skillName.trim() || undefined,
        sequence: nextSequence,
        progressContribution,
        trainingId,
        logo: logoDataUrl,
        isActive: true,
      });

      enqueueSnackbar('Módulo creado exitosamente', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Error al crear el módulo', { variant: 'error' });
      console.error('Error creating module:', error);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setSkillName('');
    setProgressContribution(100);
    setLogoDataUrl(undefined);
    setLogoFileName('');
    onClose();
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoDataUrl(reader.result as string);
      setLogoFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleClearLogo = () => {
    setLogoDataUrl(undefined);
    setLogoFileName('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nuevo Módulo</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Nombre de Habilidad"
            value={skillName}
            onChange={(e) => setSkillName(e.target.value)}
            fullWidth
            placeholder="Ej: Gestión de cultivos"
          />
          <TextField
            label="Contribución al Progreso (%)"
            type="number"
            value={progressContribution}
            onChange={(e) =>
              setProgressContribution(parseInt(e.target.value) || 0)
            }
            required
            fullWidth
            helperText="La suma de todos los módulos debe ser 100%"
            InputProps={{ 
              inputProps: { 
                min: 0, 
                max: 100,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              fullWidth
            >
              {logoFileName ? 'Cambiar logo' : 'Subir logo (opcional)'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleLogoUpload}
              />
            </Button>
            {logoFileName && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Logo seleccionado: <strong>{logoFileName}</strong>
                </Typography>
                {logoDataUrl && (
                  <Box
                    component="img"
                    src={logoDataUrl}
                    alt="Vista previa del logo"
                    sx={{ maxHeight: 120, objectFit: 'contain', borderRadius: 1 }}
                  />
                )}
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleClearLogo}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Quitar logo
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createMutation.isPending}
          startIcon={createMutation.isPending && <CircularProgress size={20} />}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateModuleModal;



